import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { 
  LogOut, 
  Menu, 
  Building2, 
  User, 
  TrendingUp, 
  DollarSign, 
  FileText, 
  FileSpreadsheet, 
  History, 
  Users, 
  Receipt, 
  Target, 
  MoreHorizontal, 
  ChevronDown, 
  Filter 
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import SideNavigation from "../components/SideNavigation";
import Breadcrumb from "../components/Breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import RevenueForm from "../components/RevenueForm";
import RestaurantManager from "../components/RestaurantManager";
import RevenueList from "../components/RevenueList";
import RevenueHistoryTabbed from "../components/RevenueHistoryTabbed";
import DashboardStats from "../components/DashboardStats";
import KPIBuilder from "../components/KPIBuilder";
import CurrencySettings from "../components/CurrencySettings";
import RevenueCategoriesManager from "../components/RevenueCategoriesManager";
import ReportsTable from "../components/ReportsTable";
import AuditLogs from "../components/AuditLogs";
import ReviewAuditPage from "../components/ReviewAuditPage";
import EmployeeManager from "../components/EmployeeManager";
import RestaurantDocuments from "../components/RestaurantDocuments";
import UserManager from "../components/UserManager";
import RoyaltyEntry from "../components/RoyaltyEntry";
import RoyaltySummary from "../components/RoyaltySummary";
import RestaurantTargetManager from "../components/RestaurantTargetManager";
import ExpenseEntry from "../components/ExpenseEntry";
import PettyCashSummary from "../components/PettyCashSummary";
import DateFilter from "../components/DateFilter";
import ProfitLossReport from "../components/ProfitLossReport";
import CashHandoverForm from "../components/CashHandoverForm";
import CashHandoverHistory from "../components/CashHandoverHistory";
import CashReconciliationReport from "../components/CashReconciliationReport";
import IssueTracker from "../components/IssueTracker";
import StoreManagerSettings from "../components/StoreManagerSettings";
import TargetAnalytics from "../components/TargetAnalytics";
import ExecutiveDashboard from "../components/ExecutiveDashboard";
import AnomalyMonitor from "../components/AnomalyMonitor";
import CredentialsManager from "../components/CredentialsManager";
import PlatformDeductions from "../components/PlatformDeductions";
import HeadOfficeExpenses from "../components/HeadOfficeExpenses";
import DataManager from "../components/DataManager";
import RestaurantCashMovement from "../components/RestaurantCashMovement";

const DashboardPage = ({ user, onLogout, onUserUpdate }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [stats, setStats] = useState(null);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    return user?.role === "restaurant_manager" ? "revenueentry" : "dashboard";
  });
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    password: "",
  });
  
  // Filter states - Load from localStorage for persistence
  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem("dashboardStartDate");
    return saved || "";
  });
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem("dashboardEndDate");
    return saved || "";
  });
  const [groupBy, setGroupBy] = useState(() => {
    const saved = localStorage.getItem("dashboardGroupBy");
    return saved || "date";
  });
  const [selectedBrand, setSelectedBrand] = useState("all");

  // Currency settings
  const [currencySettings, setCurrencySettings] = useState(() => {
    const saved = localStorage.getItem("currencySettings");
    return saved ? JSON.parse(saved) : { icon: "₹", size: "text-base", position: "before", customText: "" };
  });

  useEffect(() => {
    localStorage.setItem("currencySettings", JSON.stringify(currencySettings));
  }, [currencySettings]);

  // Save filter states to localStorage for persistence
  useEffect(() => {
    localStorage.setItem("dashboardStartDate", startDate);
  }, [startDate]);

  useEffect(() => {
    localStorage.setItem("dashboardEndDate", endDate);
  }, [endDate]);

  useEffect(() => {
    localStorage.setItem("dashboardGroupBy", groupBy);
  }, [groupBy]);

  const getUniqueBrands = () => {
    const brands = restaurants
      .map(r => r.brand)
      .filter(brand => brand && brand.trim() !== '');
    return [...new Set(brands)];
  };

  useEffect(() => {
    // Fetch data with saved filters on mount
    fetchData({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy
    });
  }, []);

  const fetchData = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.group_by) params.append('group_by', filters.group_by);

      const isRestaurantManager = user?.role === "restaurant_manager";

      if (isRestaurantManager) {
        const [restaurantsRes, categoriesRes] = await Promise.all([
          axiosInstance.get("/restaurants"),
          axiosInstance.get("/revenue-categories"),
        ]);
        setRestaurants(restaurantsRes.data);
        setRevenueCategories(categoriesRes.data);
      } else {
        const [restaurantsRes, revenuesRes, statsRes, categoriesRes, employeeStatsRes] = await Promise.all([
          axiosInstance.get("/restaurants"),
          axiosInstance.get(`/revenues?${params.toString()}`),
          axiosInstance.get(`/dashboard/stats?${params.toString()}`),
          axiosInstance.get("/revenue-categories"),
          axiosInstance.get("/employees/stats"),
        ]);
        setRestaurants(restaurantsRes.data);
        setRevenues(revenuesRes.data);
        setStats(statsRes.data);
        setRevenueCategories(categoriesRes.data);
        setEmployeeStats(employeeStatsRes.data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchData({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy
    });
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setGroupBy("date");
    fetchData();
  };

  const handleApplyKPI = (kpiConfig) => {
    // Apply KPI filters to the dashboard
    fetchData({
      start_date: kpiConfig.startDate,
      end_date: kpiConfig.endDate,
      group_by: kpiConfig.groupBy
    });
  };

  const handleCurrencyUpdate = (newSettings) => {
    setCurrencySettings(newSettings);
    // Force re-render of components that use currency display
    window.location.reload();
  };

  const handleRevenueAdded = () => {
    fetchData({ start_date: startDate, end_date: endDate, group_by: groupBy });
    toast.success("Revenue entry added successfully!");
  };

  const handleRestaurantUpdated = () => {
    fetchData({ start_date: startDate, end_date: endDate, group_by: groupBy });
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (profileData.name) updateData.name = profileData.name;
      if (profileData.email) updateData.email = profileData.email;
      if (profileData.mobile) updateData.mobile = profileData.mobile;
      if (profileData.password) updateData.password = profileData.password;

      const res = await axiosInstance.put("/auth/profile", updateData);
      if (onUserUpdate) onUserUpdate(res.data);
      toast.success("Profile updated successfully!");
      setProfileDialogOpen(false);
      setProfileData({ ...profileData, password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Side Navigation */}
      <SideNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        onUserUpdate={onUserUpdate}
        onLogout={onLogout}
        isMobileOpen={isMobileMenuOpen}
        setIsMobileOpen={setIsMobileMenuOpen}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
      />

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'} min-h-screen flex flex-col`}>
        {/* Top Header */}
        <header className="bg-white/80 backdrop-blur-xl shadow-sm border-b border-gray-200 sticky top-0 z-40">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </Button>

              {/* Spacer for mobile */}
              <div className="flex-1 lg:hidden" />

              {/* Right Side Actions */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setProfileDialogOpen(true)}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <span className="hidden sm:inline">My Profile</span>
                  <span className="sm:hidden">{user?.username?.charAt(0).toUpperCase()}</span>
                </Button>
                <Button
                  onClick={onLogout}
                  variant="outline"
                  size="sm"
                  className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumb + Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {/* Breadcrumb Navigation */}
          <Breadcrumb activeTab={activeTab} setActiveTab={setActiveTab} userRole={user?.role} />

          {/* Page Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Dashboard */}
            <TabsContent value="dashboard" className="space-y-6">
              <ExecutiveDashboard
                stats={stats}
                restaurants={restaurants}
                startDate={startDate}
                endDate={endDate}
                groupBy={groupBy}
                setGroupBy={(value) => {
                  setGroupBy(value);
                  fetchData({
                    start_date: startDate,
                    end_date: endDate,
                    group_by: value
                  });
                }}
                onFilterChange={({ startDate: newStart, endDate: newEnd }) => {
                  setStartDate(newStart);
                  setEndDate(newEnd);
                  fetchData({
                    start_date: newStart,
                    end_date: newEnd,
                    group_by: groupBy
                  });
                }}
                onNavigate={(page) => {
                  // Handle navigation to different pages
                  switch(page) {
                    case 'pl-report':
                      setActiveTab('profitloss');
                      break;
                    case 'target-analytics':
                      setActiveTab('targetanalytics');
                      break;
                    case 'cash-reconciliation':
                      setActiveTab('cashreconciliation');
                      break;
                    case 'expense-summary':
                      setActiveTab('pettycashsummary');
                      break;
                    case 'anomalies':
                      setActiveTab('anomalies');
                      break;
                    default:
                      break;
                  }
                }}
              />
            </TabsContent>

            {/* Restaurants */}
            <TabsContent value="restaurants" className="space-y-6">
              <RestaurantManager restaurants={restaurants} onUpdate={handleRestaurantUpdated} user={user} />
            </TabsContent>

            <TabsContent value="revenueentry" className="space-y-6">
              <RevenueForm
                restaurants={restaurants}
                onSuccess={handleRevenueAdded}
              />
            </TabsContent>

            <TabsContent value="revenuehistory" className="space-y-6">
              <RevenueHistoryTabbed 
                revenues={revenues} 
                restaurants={restaurants} 
                user={user}
                onUpdate={() => fetchData({ start_date: startDate, end_date: endDate, group_by: groupBy })}
              />
            </TabsContent>

            <TabsContent value="reports" className="space-y-6">
              <ReportsTable restaurants={restaurants} />
            </TabsContent>

            <TabsContent value="auditlogs" className="space-y-6">
              <AuditLogs />
            </TabsContent>

            {(user.role === "admin" || user.role === "superuser" || user.role === "manager") && (
            <TabsContent value="review_audit" className="space-y-6">
              <ReviewAuditPage user={user} restaurants={restaurants} />
            </TabsContent>
            )}

          {(user.role === "admin" || user.role === "superuser" || user.role === "manager") && (
            <TabsContent value="categories" className="pt-6" data-testid="categories-content">
              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="text-2xl">Revenue Categories</CardTitle>
                  <CardDescription>
                    Manage revenue categories for detailed reporting
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RevenueCategoriesManager
                    categories={revenueCategories}
                    restaurants={restaurants}
                    onUpdate={() => fetchData({ start_date: startDate, end_date: endDate, group_by: groupBy })}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {(user.role === "admin" || user.role === "superuser" || user.role === "manager") && (
            <TabsContent value="storemanagers" className="pt-6" data-testid="storemanagers-content">
              <StoreManagerSettings restaurants={restaurants} user={user} />
            </TabsContent>
          )}

          {(user.role === "admin" || user.role === "superuser" || (user.role === "manager" && user.permissions?.users !== "none")) && (
            <TabsContent value="users" className="pt-6" data-testid="users-content">
              <UserManager currentUser={user} />
            </TabsContent>
          )}

          <TabsContent value="employees" className="pt-6" data-testid="employees-content">
            <EmployeeManager restaurants={restaurants} />
          </TabsContent>

          <TabsContent value="documents" className="pt-6" data-testid="documents-content">
            <RestaurantDocuments restaurants={restaurants} />
          </TabsContent>

          <TabsContent value="credentials" className="pt-6" data-testid="credentials-content">
            <CredentialsManager user={user} />
          </TabsContent>

          <TabsContent value="royaltyentry" className="pt-6" data-testid="royaltyentry-content">
            <RoyaltyEntry restaurants={restaurants} />
          </TabsContent>

          <TabsContent value="royaltysummary" className="pt-6" data-testid="royaltysummary-content">
            <RoyaltySummary restaurants={restaurants} />
          </TabsContent>

          <TabsContent value="platformdeductions" className="pt-6" data-testid="platformdeductions-content">
            <PlatformDeductions restaurants={restaurants} />
          </TabsContent>

          <TabsContent value="headofficeexpenses" className="pt-6" data-testid="headofficeexpenses-content">
            <HeadOfficeExpenses user={user} />
          </TabsContent>

          <TabsContent value="targets" className="pt-6" data-testid="targets-content">
            <RestaurantTargetManager restaurants={restaurants} user={user} />
          </TabsContent>

          <TabsContent value="targetanalytics" className="pt-6" data-testid="targetanalytics-content">
            <TargetAnalytics restaurants={restaurants} user={user} />
          </TabsContent>

          <TabsContent value="expenseentry" className="pt-6" data-testid="expenseentry-content">
            <ExpenseEntry restaurants={restaurants} user={user} />
          </TabsContent>

          <TabsContent value="pettycashsummary" className="pt-6" data-testid="pettycashsummary-content">
            <PettyCashSummary restaurants={restaurants} user={user} />
          </TabsContent>

          <TabsContent value="profitloss" className="pt-6" data-testid="profitloss-content">
            <ProfitLossReport restaurants={restaurants} />
          </TabsContent>

          <TabsContent value="cashhandover" className="pt-6" data-testid="cashhandover-content">
            <CashHandoverForm user={user} />
          </TabsContent>

          <TabsContent value="cashhistory" className="pt-6" data-testid="cashhistory-content">
            <CashHandoverHistory user={user} />
          </TabsContent>

          <TabsContent value="cashreconciliation" className="pt-6" data-testid="cashreconciliation-content">
            <CashReconciliationReport user={user} />
          </TabsContent>

          <TabsContent value="issues" className="pt-6" data-testid="issues-content">
            <IssueTracker user={user} />
          </TabsContent>

          <TabsContent value="anomalies" className="pt-6" data-testid="anomalies-content">
            <AnomalyMonitor user={user} restaurants={restaurants} />
          </TabsContent>

          {(user.role === "admin" || user.role === "superuser") && (
            <TabsContent value="datamanager" className="pt-6" data-testid="datamanager-content">
              <DataManager />
            </TabsContent>
          )}

          <TabsContent value="restaurantcash" className="pt-6" data-testid="restaurantcash-content">
            <RestaurantCashMovement user={user} restaurants={restaurants} onNavigate={setActiveTab} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>My Profile</DialogTitle>
            <DialogDescription>
              Update your personal information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={user.username} disabled className="bg-gray-100" />
              <p className="text-xs text-gray-500">Username cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                placeholder="your@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Mobile Number</Label>
              <Input
                value={profileData.mobile}
                onChange={(e) => setProfileData({ ...profileData, mobile: e.target.value })}
                placeholder="+91 98765 43210"
              />
            </div>

            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={profileData.password}
                onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                placeholder="Leave blank to keep current password"
              />
              <p className="text-xs text-gray-500">Only fill if you want to change password</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProfileDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Update Profile
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default DashboardPage;